import React, { useState, useEffect } from 'react';

import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router';
import cx from 'classnames';

import { Carousel } from '@xingternal/pages-common';
import Video from '@xingternal/video';
import { IconVideo } from '@xingternal/icons';
import { GalleryItemsType, ItemType } from '../types';

import styles from './Gallery.css';

type GalleryProps = {
  galleryItems: GalleryItemsType;
  itemClicked: number | null;
};

type MediaProps = {
  imageUrl?: string;
  videoId?: string;
  pageNumber: number | null;
  index: number;
  externalUrl?: string;
  videoButtonSize: number;
};

type Pagination = {
  page: number;
};
const ExternalIcon = () => (
  <div className={styles.externalIcon}>
    <IconVideo width={30} height={30} />
  </div>
);

const Media = ({
  imageUrl,
  videoId,
  pageNumber,
  index,
  externalUrl,
}: MediaProps) => {
  const [videoActions, setVideoActions] = useState<any>(null);

  useEffect(() => {
    if (videoActions && pageNumber ? pageNumber - 1 !== index : null) {
      videoActions.pause(true);
    }
  }, [pageNumber]);

  const setPlayerActions = (api: any) => setVideoActions(api);

  return (
    <>
      {videoId && (
        <Video
          id={videoId}
          actions={setPlayerActions}
          section="entity_pages_gallery"
          onError={() => {}}
        />
      )}
      {externalUrl && (
        <a href={externalUrl} target="_blank" rel="nofollow">
          <img src={imageUrl} className={styles.image} loading="lazy" alt="Gallery image" />
          <ExternalIcon />
        </a>
      )}
      {imageUrl && !externalUrl && (
        <img src={imageUrl} className={styles.image} loading="lazy" alt="Gallery image" />
      )}
    </>
  );
};

const Gallery = ({ galleryItems, itemClicked }: GalleryProps) => {
  const location = useLocation();
  const history = useHistory();
  const [pageNumber, setPageNumber] = useState<number | null>(
    itemClicked ? itemClicked + 1 : 1
  );
  const [firstRender, setFirstRender] = useState(true);

  useEffect(() => {
    history.replace({
      ...location,
      search: `?action=openGallery&item=${itemClicked}`,
    });
    setFirstRender(false);
  }, []);

  const handlePageChange = (page: number) => {
    setPageNumber(page);
    history.replace({
      ...location,
      search: `?action=openGallery&item=${page - 1}`,
    });
  };

  return (
    <>
      <Carousel
        onPageChange={(pagination: Pagination) =>
          handlePageChange(pagination.page)
        }
        activeCard={itemClicked}
        skin={{
          carousel: cx(styles.carouselWrapper, {
            [styles.alignItemsCenter]: !firstRender,
          }),
          cardSnapStopAlignStart: styles.carousselItem,
          carouselMain: styles.carouselMain,
          removeScrollBar: styles.removeScrollBar,
          dotsContainer: styles.hide,
        }}
      >
        {galleryItems.map((item: ItemType, index: number) => (
          <div key={index} className={styles.sliderItemWrapper}>
            <div className={styles.sliderItem} data-testid={`gallery-item-${index}`}>
              <Media
                imageUrl={item.media?.url}
                videoId={item.media?.videoReferenceV2}
                externalUrl={item.media?.externalLink}
                videoButtonSize={64}
                pageNumber={pageNumber}
                index={index}
              />
            </div>
          </div>
        ))}
      </Carousel>
      <div className={styles.infoWrapper}>
        <div className={styles.pagination}>
          {`${pageNumber}/${galleryItems.length}`}
        </div>
        <div className={styles.description}>
          {pageNumber && galleryItems[pageNumber - 1]?.description}
        </div>
      </div>
    </>
  );
};

export default Gallery;
